import type { ComputedRef, CreateElement, ShallowReactive, VNode } from "vue";
import { computed } from "vue";
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import { makeRef } from "@/components/composables/reactivityTypes";
import type Guid from "@/app/types/common/guid";
import type {
  Recipe,
  RecipeHeader,
} from "@/app/services/generated/recipev2Service";
import Key from "@/app/types/common/key";
import type { ControlIn } from "@/controls/control";
import { Control, extendIn, mergeIn } from "@/controls/control";
import { ruleSet } from "@/plugins/validation";
import type { Item } from "@/app/services/generated/itemService";
import type { OutletSummary } from "@/app/services/generated/outletService";
import type { StockLocation } from "@/app/services/generated/stocklocationService";
import type { PriceList } from "@/app/services/generated/pricelistService";
import type { VatCode } from "@/app/services/generated/vatcodeService";
import { translate } from "@/plugins/i18n";
import type BaseEnumType from "@/app/services/baseEnumType";
import type { DeliveryArea } from "@/app/services/generated/deliveryareaService";
import type { Courier } from "@/app/services/generated/courierService";
import type { CollectionTime } from "@/app/services/generated/collectiontimeService";
import type { User } from "@/app/services/generated/userService";
import type {
  BatchHeader,
  ProcessHeader,
} from "@/app/services/generated/batchService";
import type ItemsCache from "@/app/caching/caches/itemsCache";
import type { BatchTemplate } from "@/app/services/generated/batchtemplateService";
import type { Group } from "@/app/services/generated/groupService";
import type { OrderSummary } from "@/app/services/generated/orderService";
import type { PalletType } from "@/app/services/generated/pallettypeService";
import type { UllageHeader } from "@/app/services/generated/ullageService";
import type { PartialQ } from "@/controls/tpcontrol";
import {
  controlDefaults,
  extendDefaults,
  TpControl,
} from "@/controls/tpcontrol";
import type { RegionalTax } from "@/app/services/generated/regionaltaxService";
import type { GlCode } from "@/app/services/generated/glcodeService";
import type { OutletType } from "@/app/services/generated/outlettypeService";
import type { OutletRating } from "@/app/services/generated/outletratingService";
import type { SalesArea } from "@/app/services/generated/salesareaService";
import type { SalesCode } from "@/app/services/generated/salescodeService";
import type { OutletSubType } from "@/app/services/generated/outletsubtypeService";
import type { BmUiState } from "@/components/controls/input/state/BmUIStateRepository";
import type { BomHeader } from "@/app/services/generated/eprbomService";
import type { DocumentTemplate } from "@/app/services/generated/documenttemplateService";

type DdlGeneralEntry<TValue> = {
  readonly display: string;
  readonly id: string;
  readonly value: TValue;
  readonly isHidden: boolean;
};

type DdlGeneralIn<TItem> = ControlIn & {
  items: readonly TItem[];
  translateKey?: Key;
  label: string;
  required: boolean;
  loading: boolean;
  autoSort: boolean;
  implySelectOnlyOption: boolean;
};

export class DdlGeneral<TItem, TValue> extends Control {
  private _selectedId = makeRef<string | null>(null);
  private _entries: ComputedRef<readonly DdlGeneralEntry<TValue>[]>;
  private _extractEntry: (item: TItem) => DdlGeneralEntry<TValue>;
  public onInput: () => void = () => {};

  protected _selectedGeneralEntry = computed(() => {
    const id = this._selectedId.value;
    return id === null
      ? null
      : (this._entries.value.find((x) => x.id === id) ?? null);
  });

  #in: ShallowReactive<DdlGeneralIn<TItem>>;

  protected _implicitOnlyOption = computed(() =>
    this.#in.implySelectOnlyOption && this._entries.value.length === 1
      ? this._entries.value[0]
      : null
  );

  protected _val = computed(() => {
    return (
      (this._selectedGeneralEntry.value ?? this._implicitOnlyOption.value)
        ?.value ?? null
    );
  });

  protected selectId(id: string | null) {
    if (this._selectedId.value !== id) {
      this._selectedId.value = id;
    }
  }

  public selectItem(item: TItem) {
    this.selectId(this._extractEntry(item).id);
  }

  get val() {
    return this._val.value;
  }

  get in() {
    return this.#in;
  }

  get hasSelection() {
    return !!this._selectedId.value;
  }

  protected valueInput() {
    this.onInput();
  }

  constructor(extractEntry: (item: TItem) => DdlGeneralEntry<TValue>) {
    super();
    this._extractEntry = extractEntry;
    this.#in = extendIn(super.in, {
      items: [],
      translateKey: undefined,
      label: "",
      required: false,
      loading: false,
      readOnly: false,
      autoSort: true,
      implySelectOnlyOption: false,
    });
    this._entries = computed(() => this.#in.items.map((x) => extractEntry(x)));
  }

  // Our BmDropDown does a weird map to Guid if the value looks like a Guid, which we don't really want
  // but for now just toString() it back.
  private baseInputHandler(
    this: DdlGeneral<TItem, TValue>,
    event: Guid | string | null
  ) {
    const chosenId = event?.toString();
    this.selectId(chosenId ?? null);
    this.valueInput();
  }

  render(h: CreateElement): VNode {
    const inp = (ev: any) => this.baseInputHandler(ev);
    const implicitOption = this._implicitOnlyOption.value;
    return h(BmDropdown, {
      props: {
        value: this._selectedGeneralEntry.value?.id,
        items: this._entries.value,
        itemText: (x: DdlGeneralEntry<TValue>) => x.display,
        itemValue: (x: DdlGeneralEntry<TValue>) => x.id,
        itemHidden: (x: DdlGeneralEntry<TValue>) => x.isHidden,
        t: this.in.translateKey,
        label: this.in.label,
        rules: this.in.required && !implicitOption ? [ruleSet.required()] : [],
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        clearable: !this.in.required,
        sortItems: this.in.autoSort,
        placeholder: implicitOption?.display ?? "",
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

export class DdlGuidQ<TItem> extends DdlGeneral<TItem, Guid> {
  constructor(extracts: DdlExtracts<TItem>) {
    super((item) => {
      return {
        display: extracts.extractTexts(item).join(" | "),
        id: extracts.extractId(item).toString(),
        value: extracts.extractId(item),
        isHidden: extracts.extractIsHidden(item),
      };
    });
  }

  setValue(val: Guid | null) {
    super.selectId(val?.toString() ?? null);
  }
}

export class DdlGuid<TItem> extends DdlGuidQ<TItem> {
  constructor(extracts: DdlExtracts<TItem>) {
    super(extracts);
    this.in.required = true;
  }

  // There is a general difficulty with most of our guid-based dropdowns, in that we will usually
  // never be certain that the DDL will contain any valid entries. Therefore our default here
  // is that when setting the value we expect to bind from something which definitely has a guid
  // but when getting the value of the DDL we may get null and the owner has to handle this explicitly.
  setValue(val: Guid) {
    super.setValue(val);
  }

  setValueOrClear(val: Guid | null) {
    super.setValue(val);
  }
}

export class DdlBool extends DdlGeneral<boolean, boolean> {
  constructor(falseText: string, trueText: string) {
    super((b) => {
      return {
        display: b ? trueText : falseText,
        id: b ? "1" : "0",
        value: b,
        isHidden: false,
      };
    });
    this.in.items = [false, true];
    this.in.required = true;
    super.selectId("0");
  }
  setValue(val: boolean) {
    super.selectId(val ? "1" : "0");
  }
  get val() {
    return this._val.value ?? false;
  }
}

export class DdlEnumQ<TEnum extends BaseEnumType> extends DdlGeneral<
  TEnum,
  TEnum
> {
  protected _allValues: readonly TEnum[];

  constructor(
    tEnum: { getValues: () => readonly TEnum[] },
    describe: (item: TEnum) => string = (item: TEnum) =>
      translate(Key.fromString(item.translationKey ?? ""))
  ) {
    super((e) => {
      return {
        display: describe(e),
        id: e.value.toString(),
        value: e,
        isHidden: false,
      };
    });
    this._allValues = tEnum.getValues();
    this.in.items = this._allValues;
  }
  setValue(val: TEnum) {
    super.selectId(val.value.toString());
  }
}

export class DdlEnum<TEnum extends BaseEnumType> extends DdlEnumQ<TEnum> {
  #lastVal: TEnum | null = null;
  constructor(
    tEnum: { getValues: () => readonly TEnum[] },
    describe?: (item: TEnum) => string
  ) {
    super(tEnum, describe);
    this.in.required = true;
  }

  setValue(val: TEnum) {
    super.setValue(val);
    this.#lastVal = val;
  }

  get val() {
    return super.val ?? this.#lastVal ?? this._allValues[0];
  }

  protected valueInput() {
    if (super.val) {
      this.#lastVal = super.val;
    }
    this.onInput();
  }
}

export type DdlExtracts<TItem> = {
  extractId: (item: TItem) => Guid;
  extractTexts: (item: TItem) => readonly string[];
  extractIsHidden: (item: TItem) => boolean;
};

type IdName = {
  id: Guid;
  name: string;
};
type IdNameAndHidden = IdName & {
  isHidden: boolean;
};
type HeaderWithIdNameAndHidden = {
  header: IdNameAndHidden;
};

const idNameAndHidden: DdlExtracts<IdNameAndHidden> = {
  extractId: (item) => item.id,
  extractTexts: (item) => [item.name],
  extractIsHidden: (item) => item.isHidden,
};

const idNameNoHidden: DdlExtracts<IdName> = {
  extractId: (item) => item.id,
  extractTexts: (item) => [item.name],
  extractIsHidden: (item) => false,
};

const headerIdNameAndHidden: DdlExtracts<HeaderWithIdNameAndHidden> = {
  extractId: (item) => item.header.id,
  extractTexts: (item) => [item.header.name],
  extractIsHidden: (item) => item.header.isHidden,
};

const outletSummaryExtracts: DdlExtracts<OutletSummary> = {
  extractId: (item) => item.id,
  extractTexts: (item) => [item.displayName, item.code],
  extractIsHidden: (item) => item.isHidden,
};

const itemExtracts: DdlExtracts<Item> = {
  extractId: (item) => item.details.id,
  extractTexts: (item) => [item.details.name, item.details.code],
  extractIsHidden: (item) => item.details.isHidden,
};

const orderSummaryExtracts: DdlExtracts<OrderSummary> = {
  extractId: (item) => item.id,
  extractTexts: (item) => [item.orderNumber],
  extractIsHidden: () => false,
};

const ullageHeaderExtracts: DdlExtracts<UllageHeader> = {
  extractId: (item) => item.id,
  extractTexts: (item) => [item.readOnly.ullageNumber],
  extractIsHidden: () => false,
};

function makeEnumDdl<TEnum extends BaseEnumType>(
  param: readonly TEnum[],
  opts: Partial<DdlGeneralIn<TEnum>>,
  describe?: (item: TEnum) => string
): DdlEnum<TEnum>;
function makeEnumDdl<TEnum extends BaseEnumType>(
  param: {
    getValues: () => readonly TEnum[];
  },
  opts: Partial<DdlGeneralIn<TEnum>>,
  describe?: (item: TEnum) => string
): DdlEnum<TEnum>;
function makeEnumDdl<TEnum extends BaseEnumType>(
  param:
    | {
        getValues: () => readonly TEnum[];
      }
    | readonly TEnum[],
  opts: Partial<DdlGeneralIn<TEnum>>,
  describe?: (item: TEnum) => string
) {
  if (Array.isArray(param)) {
    return mergeIn(
      new DdlEnum<TEnum>({ getValues: () => param }, describe),
      opts
    );
  }
  return mergeIn(
    new DdlEnum<TEnum>(
      param as {
        getValues: () => readonly TEnum[];
      },
      describe
    ),
    opts
  );
}

type CacheWithItems = {
  items: ItemsCache;
};

const batchHeaderUrnExtract: DdlExtracts<BatchHeader> = {
  extractId: (x) => x.id,
  extractIsHidden: (x) => x.isHidden,
  extractTexts: (x) => [x.urn],
};

function batchHeaderUrnProduceExtract(
  cache: CacheWithItems
): DdlExtracts<BatchHeader> {
  return {
    extractId: (x) => x.id,
    extractIsHidden: (x) => x.isHidden,
    extractTexts: (x) => [
      x.urn,
      cache.items.getById(x.materialProducedStockItemId)?.details.name ?? "",
    ],
  };
}

function guidWithCustomExtracts<TItem>(
  extracts: DdlExtracts<TItem>,
  opts: Partial<DdlGeneralIn<TItem>>
) {
  return mergeIn(new DdlGuid<TItem>(extracts), opts);
}

const ddls = {
  batchHeader: (opts: Partial<DdlGeneralIn<BatchHeader>>) =>
    mergeIn(new DdlGuid<BatchHeader>(batchHeaderUrnExtract), opts),
  batchHeaderWithProduce: (
    cache: CacheWithItems,
    opts?: Partial<DdlGeneralIn<BatchHeader>>
  ) =>
    mergeIn(
      new DdlGuid<BatchHeader>(batchHeaderUrnProduceExtract(cache)),
      opts
    ),
  batchTemplate: () => new DdlGuid<BatchTemplate>(headerIdNameAndHidden),
  boolean: (
    falseText: string,
    trueText: string,
    opts?: Partial<DdlGeneralIn<boolean>>
  ) => mergeIn(new DdlBool(falseText, trueText), opts),
  collectionTimeQ: () => new DdlGuidQ<CollectionTime>(idNameNoHidden),
  courier: () => new DdlGuid<Courier>(idNameAndHidden),
  deliveryArea: () => new DdlGuid<DeliveryArea>(idNameAndHidden),
  enum: makeEnumDdl,
  glCode: (opts?: Partial<DdlGeneralIn<GlCode>>) =>
    mergeIn(new DdlGuid<GlCode>(idNameAndHidden), opts),
  groupQ: (opts: Partial<DdlGeneralIn<Group>>) =>
    mergeIn(new DdlGuidQ<Group>(idNameAndHidden), opts),
  guidWithCustomExtracts,
  itemQ: (opts?: Partial<DdlGeneralIn<Item>>) =>
    mergeIn(new DdlGuidQ<Item>(itemExtracts), opts),
  item: (opts: Partial<DdlGeneralIn<Item>>) =>
    mergeIn(new DdlGuid<Item>(itemExtracts), opts),
  orderSummaryQ: (opts: Partial<DdlGeneralIn<OrderSummary>>) =>
    mergeIn(new DdlGuidQ(orderSummaryExtracts), opts),
  outletSummaryQ: (opts: Partial<DdlGeneralIn<OutletSummary>>) =>
    mergeIn(new DdlGuidQ<OutletSummary>(outletSummaryExtracts), opts),
  outletSummary: (opts?: Partial<DdlGeneralIn<OutletSummary>>) =>
    mergeIn(new DdlGuid<OutletSummary>(outletSummaryExtracts), opts),
  palletType: (opts: Partial<DdlGeneralIn<PalletType>>) =>
    mergeIn(new DdlGuid<PalletType>(idNameAndHidden), opts),
  priceList: (opts?: Partial<DdlGeneralIn<PriceList>>) =>
    mergeIn(new DdlGuid<PriceList>(idNameAndHidden), opts),
  processHeader: (opts: Partial<DdlGeneralIn<ProcessHeader>>) =>
    mergeIn(new DdlGuid<ProcessHeader>(idNameNoHidden), opts),
  stockLocationQ: () => new DdlGuidQ<StockLocation>(idNameAndHidden),
  stockLocation: (opts?: Partial<DdlGeneralIn<StockLocation>>) =>
    mergeIn(new DdlGuid<StockLocation>(idNameAndHidden), opts),
  recipe: () => new DdlGuid<Recipe>(headerIdNameAndHidden),
  recipeHeader: () => new DdlGuid<RecipeHeader>(idNameAndHidden),
  recipeHeaderQ: () => new DdlGuidQ<RecipeHeader>(idNameAndHidden),
  ullageHeaderQ: (opts: Partial<DdlGeneralIn<UllageHeader>>) =>
    mergeIn(new DdlGuidQ<UllageHeader>(ullageHeaderExtracts), opts),
  vatCode: (opts?: Partial<DdlGeneralIn<VatCode>>) =>
    mergeIn(new DdlGuid<VatCode>(idNameAndHidden), opts),
  user: (opts?: Partial<DdlGeneralIn<User>>) =>
    mergeIn(new DdlGuid<User>(idNameAndHidden), opts ?? {}),
  userQ: (opts: Partial<DdlGeneralIn<User>>) =>
    mergeIn(new DdlGuidQ<User>(idNameAndHidden), opts),
  eprBom: (opts?: Partial<DdlGeneralIn<BomHeader>>) =>
    mergeIn(new DdlGuid<BomHeader>(idNameAndHidden), opts ?? {}),
  document: (opts?: Partial<DdlGeneralIn<DocumentTemplate>>) =>
    mergeIn(new DdlGuid<DocumentTemplate>(idNameAndHidden), opts ?? {}),
};

const ddlExtracts = {
  outletSummaryExtracts,
};

const multiSelectGeneralDefaults = extendDefaults(controlDefaults, {
  translateKey: null as Key | null,
  label: "",
  loading: false,
  autoSort: true,
  showSelectAll: false,
});

type MultiSelectGeneralIn<TItem, TValue> = typeof multiSelectGeneralDefaults & {
  onInput: (val: readonly TValue[]) => void;
  items: readonly TItem[];
};

export class MultiSelectGeneral<TItem, TValue> extends TpControl<
  MultiSelectGeneralIn<TItem, TValue>
> {
  private _selectedIds = makeRef<readonly string[]>([]);
  private _entries: ComputedRef<readonly DdlGeneralEntry<TValue>[]>;

  protected _val = computed(() => {
    const ids = new Set(this._selectedIds.value);
    return this._entries.value.filter((x) => ids.has(x.id)).map((x) => x.value);
  });

  public get selectedIds() {
    return this._selectedIds.value;
  }
  public set selectedIds(ids: readonly string[]) {
    // Deduplicate while setting.
    this._selectedIds.value = [...new Set(ids)];
  }

  public restoreUiState(state: BmUiState, keyName?: string) {
    keyName = keyName || this.in.translateKey?.value;
    if (keyName) {
      const stateVal = state[keyName];
      if (Array.isArray(stateVal)) {
        const restoreIds = new Set(stateVal.map((x) => x.toString()));
        this.selectedIds = this._entries.value
          .map((x) => x.id)
          .filter((x) => restoreIds.has(x));
      }
    }
  }

  public storeUiState(state: BmUiState, keyName?: string) {
    keyName = keyName || this.in.translateKey?.value;
    if (keyName) {
      state[keyName] = this.selectedIds;
    }
  }

  get val() {
    return this._val.value;
  }

  constructor(
    extractEntry: (item: TItem) => DdlGeneralEntry<TValue>,
    ...opts: PartialQ<MultiSelectGeneralIn<TItem, TValue>>[]
  ) {
    super(
      extendDefaults(multiSelectGeneralDefaults, {
        items: [],
        onInput: () => {},
      }),
      ...opts
    );
    this._entries = computed(() => this.in.items.map((x) => extractEntry(x)));
  }

  // Our BmDropDown does a weird map to Guid if the value looks like a Guid, which we don't really want
  // but for now just toString() it back.
  private baseInputHandler(selection: readonly (Guid | string)[]) {
    this._selectedIds.value = selection.map((x) => x.toString());
    this.in.onInput(this._val.value);
  }

  render(h: CreateElement): VNode {
    const inp = (ev: readonly (Guid | string)[]) => this.baseInputHandler(ev);
    return h(BmDropdown, {
      key: this.vueKey,
      props: {
        value: this._selectedIds.value,
        items: this._entries.value,
        itemText: (x: DdlGeneralEntry<TValue>) => x.display,
        itemValue: (x: DdlGeneralEntry<TValue>) => x.id,
        itemHidden: (x: DdlGeneralEntry<TValue>) => x.isHidden,
        t: this.in.translateKey,
        label: this.in.label,
        disabled: !this.resolveEnabled(),
        loading: this.in.loading,
        readOnly: this.resolveReadOnly(),
        sortItems: this.in.autoSort,
        multiple: true,
        showSelectAll: this.in.showSelectAll,
      },
      on: {
        "update:value": inp,
      },
    });
  }
}

type MultiSelectGuidIn<TItem> = MultiSelectGeneralIn<TItem, Guid>;

export class MultiSelectGuid<TItem> extends MultiSelectGeneral<TItem, Guid> {
  constructor(extracts: DdlExtracts<TItem>) {
    super((item) => {
      return {
        display: extracts.extractTexts(item).join(" | "),
        id: extracts.extractId(item).toString(),
        value: extracts.extractId(item),
        isHidden: extracts.extractIsHidden(item),
      };
    });
  }

  setValue(val: readonly Guid[]) {
    super.selectedIds = val.map((x) => x.toString());
  }
}

export class MultiSelectEnum<
  TEnum extends BaseEnumType,
> extends MultiSelectGeneral<TEnum, TEnum> {
  constructor() {
    super((item) => {
      return {
        display: translate(Key.fromString(item.translationKey ?? "")),
        id: item.key.toString(),
        value: item,
        isHidden: false,
      };
    });
  }

  setValue(val: readonly TEnum[]) {
    super.selectedIds = val.map((x) => x.key.toString());
  }
}

const multiSelects = {
  groups: (opts?: Partial<MultiSelectGuidIn<Group>>) =>
    mergeIn(new MultiSelectGuid<Group>(idNameAndHidden), opts),

  translationKeys: (opts?: Partial<MultiSelectGuidIn<Key>>) =>
    mergeIn(
      new MultiSelectGeneral<Key, Key>((item) => ({
        display: translate(item),
        id: item.value,
        value: item,
        isHidden: false,
      })),
      opts
    ),
  outletRatings: (opts?: Partial<MultiSelectGuidIn<OutletRating>>) =>
    mergeIn(new MultiSelectGuid<OutletRating>(idNameAndHidden), opts),
  outletSubTypes: (opts?: Partial<MultiSelectGuidIn<OutletSubType>>) =>
    mergeIn(new MultiSelectGuid<OutletSubType>(idNameAndHidden), opts),
  outletTypes: (opts?: Partial<MultiSelectGuidIn<OutletType>>) =>
    mergeIn(new MultiSelectGuid<OutletType>(idNameAndHidden), opts),
  regionalTaxes: (opts?: Partial<MultiSelectGuidIn<RegionalTax>>) =>
    mergeIn(new MultiSelectGuid<RegionalTax>(idNameAndHidden), opts),
  salesAreas: (opts?: Partial<MultiSelectGuidIn<SalesArea>>) =>
    mergeIn(new MultiSelectGuid<SalesArea>(idNameAndHidden), opts),
  salesCodes: (opts?: Partial<MultiSelectGuidIn<SalesCode>>) =>
    mergeIn(new MultiSelectGuid<SalesCode>(idNameAndHidden), opts),
  enum: <TEnum extends BaseEnumType>(
    opts?: Partial<MultiSelectGeneralIn<TEnum, TEnum>>
  ) => mergeIn(new MultiSelectEnum<TEnum>(), opts),
};

export { ddls, ddlExtracts, multiSelects };
